import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useToast } from '@/components/ui/use-toast';
import { LogVolumeSVGChart } from '@/components/LogVolumeSVGChart';
import { useTouchNavigation } from '@/hooks/useTouchNavigation';
import { CalendarIcon, Download, RefreshCw, Search, Filter, Zap, AlertTriangle, Info, Bug, ShieldAlert, ChevronLeft, ChevronRight, Laptop } from 'lucide-react';
import { format, subDays, startOfDay, endOfDay } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { Link } from 'react-router-dom';

interface LogEntry {
  id: string;
  timestamp: string;
  source_type: string;
  event_type: string;
  message: string;
  log_level: string;
  ai_category?: string;
  ai_severity_score?: number;
  ai_summary?: string;
  ai_tags?: string[];
  metadata?: any;
  user_id?: string;
  device_id?: string;
  duration_ms?: number;
  status_code?: number;
  devices?: {
    id: string;
    name: string;
    hostname: string;
  } | null;
}

interface ChartDataPoint {
  timestamp: string;
  count: number;
  date: string;
}

const LogsPage = () => {
  const { toast } = useToast();
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [filteredLogs, setFilteredLogs] = useState<LogEntry[]>([]);
  const [chartData, setChartData] = useState<ChartDataPoint[]>([]);
  const [allChartData, setAllChartData] = useState<ChartDataPoint[]>([]);
  const [chartWindowStart, setChartWindowStart] = useState(0);
  const [chartWindowSize] = useState(14); // Show 14 days at a time
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLevel, setSelectedLevel] = useState('all');
  const [selectedSource, setSelectedSource] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedDevice, setSelectedDevice] = useState('all');
  const [dateRange, setDateRange] = useState({
    from: startOfDay(subDays(new Date(), 30)),
    to: endOfDay(new Date())
  });
  const [isProcessingAI, setIsProcessingAI] = useState(false);

  const logLevelColors = {
    debug: 'bg-slate-100 text-slate-800',
    info: 'bg-blue-100 text-blue-800', 
    warn: 'bg-yellow-100 text-yellow-800',
    error: 'bg-red-100 text-red-800',
    critical: 'bg-red-600 text-white'
  };

  const logLevelIcons = {
    debug: Bug,
    info: Info,
    warn: AlertTriangle,
    error: ShieldAlert,
    critical: ShieldAlert
  };

  const sourceTypeLabels = {
    identity_provider: 'Identity Provider',
    abm: 'Apple Business Manager',
    apns: 'Apple Push Notifications',
    device_communication: 'Device Communication',
    auth: 'Authentication',
    edge_function: 'Edge Functions'
  };

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('integration_sync_logs')
        .select(`
          *,
          devices!integration_sync_logs_device_id_fkey (
            id,
            name,
            hostname
          )
        `)
        .gte('timestamp', dateRange.from.toISOString())
        .lte('timestamp', dateRange.to.toISOString())
        .order('timestamp', { ascending: false })
        .limit(1000);

      if (error) throw error;

      if (data && data.length > 0) {
        // Transform the data to match our LogEntry type
        const transformedData = data.map(log => ({
          ...log,
          devices: Array.isArray(log.devices) ? log.devices[0] : log.devices
        })) as LogEntry[];
        setLogs(transformedData);
        return;
      }

      // Fallback to sample data if no real data
      const sampleLogs = getSampleLogs();
      setLogs(sampleLogs);
    } catch (error) {
      console.error('Error fetching logs:', error);
      const sampleLogs = getSampleLogs();
      setLogs(sampleLogs);
    } finally {
      setLoading(false);
    }
  };

  // Sample data to show users how the interface looks
  const getSampleLogs = (): LogEntry[] => {
    const logs: LogEntry[] = [];
    const sources = ['apns', 'identity_provider', 'device_communication', 'abm', 'edge_function', 'auth'];
    const levels = ['debug', 'info', 'warn', 'error', 'critical'];
    const categories = ['security', 'integration', 'monitoring', 'authentication', 'deployment', 'performance'];
    
    // Use the current dateRange instead of hardcoded 30 days
    const startDate = dateRange.from;
    const endDate = dateRange.to;
    const totalDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
    
    for (let dayOffset = 0; dayOffset <= totalDays; dayOffset++) {
      const currentDay = new Date(startDate.getTime() + dayOffset * 24 * 60 * 60 * 1000);
      const isWeekend = currentDay.getDay() === 0 || currentDay.getDay() === 6;
      
      // Determine daily log count (fewer on weekends, scale based on selected period)
      const baseMultiplier = totalDays <= 1 ? 3 : totalDays <= 7 ? 2 : 1; // More logs for shorter periods
      const baseDailyLogs = (isWeekend ? 20 + Math.floor(Math.random() * 30) : 40 + Math.floor(Math.random() * 80)) * baseMultiplier;
      
      // Create deployment/maintenance days with more activity (5% chance)
      const isMaintenanceDay = Math.random() < 0.05;
      const dailyLogCount = isMaintenanceDay ? baseDailyLogs * 3 : baseDailyLogs;
      
      for (let logIndex = 0; logIndex < dailyLogCount; logIndex++) {
        // Distribute logs throughout the day with business hours pattern
        const randomHour = Math.random() * 24;
        
        // Business hours multiplier (more logs during 6-22, peak at 9-17)
        let timeMultiplier = 1;
        if (randomHour >= 9 && randomHour <= 17) {
          timeMultiplier = 2.5; // Peak business hours
        } else if (randomHour >= 6 && randomHour <= 22) {
          timeMultiplier = 1.5; // Extended business hours
        } else {
          timeMultiplier = 0.3; // Night time - minimal activity
        }
        
        // Skip some entries based on time multiplier to create realistic patterns
        if (Math.random() > timeMultiplier * 0.4) continue;
        
        const randomMinute = Math.random() * 60;
        const randomSecond = Math.random() * 60;
        
        const logTimestamp = new Date(
          currentDay.getFullYear(),
          currentDay.getMonth(),
          currentDay.getDate(),
          Math.floor(randomHour),
          Math.floor(randomMinute),
          Math.floor(randomSecond)
        );
        
        const source = sources[Math.floor(Math.random() * sources.length)];
        let level = levels[Math.floor(Math.random() * levels.length)];
        
        // Maintenance days have more errors and warnings
        if (isMaintenanceDay && Math.random() < 0.3) {
          level = Math.random() < 0.5 ? 'warn' : 'error';
        }
        
        const category = categories[Math.floor(Math.random() * categories.length)];
        
        logs.push({
          id: `sample-${dayOffset}-${logIndex}`,
          timestamp: logTimestamp.toISOString(),
          source_type: source,
          event_type: getRandomEventType(source),
          message: getRandomMessage(source, level, isMaintenanceDay),
          log_level: level,
          ai_category: category,
          ai_severity_score: getSeverityScore(level),
          ai_summary: getRandomSummary(level, isMaintenanceDay),
          ai_tags: getRandomTags(source),
          duration_ms: Math.floor(Math.random() * 5000) + 10,
          status_code: level === 'error' || level === 'critical' ? 500 : 200,
          ...(source === 'device_communication' && { device_id: `device-${Math.floor(Math.random() * 100)}` }),
          ...(source === 'auth' && { user_id: `user-${Math.floor(Math.random() * 50)}` })
        });
      }
    }
    
    return logs.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  };

  const getRandomEventType = (source: string): string => {
    const eventTypes = {
      apns: ['certificate_validation', 'push_notification_sent', 'connection_established', 'token_refresh'],
      identity_provider: ['user_sync', 'group_sync', 'authentication', 'authorization_check'],
      device_communication: ['heartbeat_received', 'command_sent', 'response_received', 'status_update'],
      abm: ['token_refresh', 'device_assignment', 'profile_deployment', 'enrollment_check'],
      edge_function: ['function_execution', 'deployment', 'error_handling', 'timeout'],
      auth: ['login', 'logout', 'token_validation', 'permission_check']
    };
    const types = eventTypes[source as keyof typeof eventTypes] || ['general_event'];
    return types[Math.floor(Math.random() * types.length)];
  };

  const getRandomMessage = (source: string, level: string, isMaintenanceDay?: boolean): string => {
    const messages = {
      info: [
        'Operation completed successfully',
        'Service is running normally',
        'Configuration updated',
        'Connection established'
      ],
      warn: [
        'High memory usage detected',
        'Slow response time observed',
        'Retry attempt initiated',
        'Cache miss rate elevated'
      ],
      error: [
        'Connection timeout occurred',
        'Authentication failed',
        'Resource not found',
        'Invalid configuration detected'
      ],
      critical: [
        'Service completely unavailable',
        'Data corruption detected',
        'Security breach attempt',
        'System failure imminent'
      ],
      debug: [
        'Debug trace completed',
        'Variable state logged',
        'Function entry point',
        'Memory allocation tracked'
      ]
    };
    const levelMessages = messages[level as keyof typeof messages] || messages.info;
    return levelMessages[Math.floor(Math.random() * levelMessages.length)];
  };

  const getSeverityScore = (level: string): number => {
    const scores = { debug: 1, info: 2, warn: 5, error: 7, critical: 9 };
    return scores[level as keyof typeof scores] || 1;
  };

  const getRandomSummary = (level: string, isMaintenanceDay?: boolean): string => {
    const summaries = {
      info: 'Normal operation completed',
      warn: 'Potential issue detected',
      error: 'Operation failed',
      critical: 'Critical system failure',
      debug: 'Debug information logged'
    };
    return summaries[level as keyof typeof summaries] || 'Event logged';
  };

  const getRandomTags = (source: string): string[] => {
    const tagSets = {
      apns: [['notification', 'push'], ['certificate', 'ssl'], ['connection', 'network']],
      identity_provider: [['sync', 'users'], ['authentication', 'ldap'], ['directory', 'active-directory']],
      device_communication: [['heartbeat', 'status'], ['command', 'response'], ['monitoring', 'health']],
      abm: [['enrollment', 'device'], ['profile', 'configuration'], ['apple', 'business']],
      edge_function: [['serverless', 'function'], ['deployment', 'build'], ['execution', 'runtime']],
      auth: [['login', 'session'], ['token', 'jwt'], ['permission', 'role']]
    };
    const sets = tagSets[source as keyof typeof tagSets] || [['general', 'system']];
    return sets[Math.floor(Math.random() * sets.length)];
  };

  const generateChartData = (logData: LogEntry[]) => {
    const dailyData = new Map<string, number>();
    
    // Initialize with 0 counts for all days in range
    const days = Math.ceil((dateRange.to.getTime() - dateRange.from.getTime()) / (1000 * 60 * 60 * 24));
    for (let i = 0; i <= days; i++) {
      const day = new Date(dateRange.from.getTime() + i * 24 * 60 * 60 * 1000);
      const key = format(day, 'yyyy-MM-dd');
      dailyData.set(key, 0);
    }

    // Count logs per day
    logData.forEach(log => {
      const day = format(new Date(log.timestamp), 'yyyy-MM-dd');
      dailyData.set(day, (dailyData.get(day) || 0) + 1);
    });

    const chartPoints = Array.from(dailyData.entries())
      .map(([timestamp, count]) => ({
        timestamp,
        count,
        date: format(new Date(timestamp), 'MMM dd')
      }))
      .sort((a, b) => a.timestamp.localeCompare(b.timestamp));

    setAllChartData(chartPoints);
    updateChartWindow(chartPoints, chartWindowStart);
  };

  const updateChartWindow = (data: ChartDataPoint[], startIndex: number) => {
    const windowData = data.slice(startIndex, startIndex + chartWindowSize);
    setChartData(windowData);
  };

  const handlePreviousWindow = () => {
    const newStart = Math.max(0, chartWindowStart - chartWindowSize);
    setChartWindowStart(newStart);
    updateChartWindow(allChartData, newStart);
  };

  const handleNextWindow = () => {
    const maxStart = Math.max(0, allChartData.length - chartWindowSize);
    const newStart = Math.min(maxStart, chartWindowStart + chartWindowSize);
    setChartWindowStart(newStart);
    updateChartWindow(allChartData, newStart);
  };

  const handleQuickNavigation = (type: 'today' | 'week' | 'month') => {
    let newDateRange;
    switch (type) {
      case 'today':
        newDateRange = { from: startOfDay(new Date()), to: endOfDay(new Date()) };
        break;
      case 'week':
        newDateRange = { from: startOfDay(subDays(new Date(), 6)), to: endOfDay(new Date()) };
        break;
      case 'month':
        newDateRange = { from: startOfDay(subDays(new Date(), 29)), to: endOfDay(new Date()) };
        break;
    }
    setDateRange(newDateRange);
    setChartWindowStart(0); // Reset to beginning
  };

  const processLogsWithAI = async (logIds: string[]) => {
    if (logIds.length === 0) return;
    
    setIsProcessingAI(true);
    try {
      const { data, error } = await supabase.functions.invoke('process-logs-ai', {
        body: { logIds }
      });

      if (error) throw error;

      // Silently update logs with AI enhancements
      if (data?.processed > 0) {
        // Refresh logs to get AI enhancements without showing toast
        const { data: updatedData, error: fetchError } = await supabase
          .from('integration_sync_logs')
          .select('*')
          .gte('timestamp', dateRange.from.toISOString())
          .lte('timestamp', dateRange.to.toISOString())
          .order('timestamp', { ascending: false })
          .limit(1000);

        if (!fetchError && updatedData) {
          setLogs(updatedData);
          generateChartData(updatedData);
        }
      }
    } catch (error) {
      console.error('Error processing logs with AI:', error);
      // Only show error toast, not success toast for automatic processing
    } finally {
      setIsProcessingAI(false);
    }
  };

  const exportLogs = () => {
    const csv = [
      ['Timestamp', 'Source', 'Level', 'Event', 'Device Name', 'Hostname', 'Message', 'AI Category', 'AI Summary'],
      ...filteredLogs.map(log => [
        log.timestamp,
        sourceTypeLabels[log.source_type as keyof typeof sourceTypeLabels] || log.source_type,
        log.log_level,
        log.event_type,
        log.devices?.name || '',
        log.devices?.hostname || '',
        log.message,
        log.ai_category || '',
        log.ai_summary || ''
      ])
    ].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `logs-${format(new Date(), 'yyyy-MM-dd-HHmm')}.csv`;
    a.click();
    URL.revokeObjectURL(url);

    toast({
      title: "Export Complete",
      description: "Logs exported successfully",
    });
  };

  // Filter logs based on current filters
  useEffect(() => {
    let filtered = logs;

    if (searchTerm) {
      filtered = filtered.filter(log => 
        log.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.event_type.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (log.ai_summary && log.ai_summary.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (log.devices?.name && log.devices.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (log.devices?.hostname && log.devices.hostname.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    if (selectedLevel !== 'all') {
      filtered = filtered.filter(log => log.log_level === selectedLevel);
    }

    if (selectedSource !== 'all') {
      filtered = filtered.filter(log => log.source_type === selectedSource);
    }

    if (selectedCategory !== 'all') {
      filtered = filtered.filter(log => log.ai_category === selectedCategory);
    }

    if (selectedDevice !== 'all') {
      filtered = filtered.filter(log => log.device_id === selectedDevice);
    }

    setFilteredLogs(filtered);
  }, [logs, searchTerm, selectedLevel, selectedSource, selectedCategory, selectedDevice]);

  // Get unique categories for filter
  const uniqueCategories = useMemo(() => {
    return Array.from(new Set(logs.map(log => log.ai_category).filter(Boolean)));
  }, [logs]);

  // Get unique devices for filter
  const uniqueDevices = useMemo(() => {
    const deviceMap = new Map<string, { id: string; name: string }>();
    logs.forEach(log => {
      if (log.device_id && log.devices) {
        deviceMap.set(log.device_id, {
          id: log.device_id,
          name: log.devices.name
        });
      }
    });
    return Array.from(deviceMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [logs]);

  useEffect(() => {
    fetchLogs();
  }, [dateRange]);

  // Generate chart data whenever logs change
  useEffect(() => {
    if (logs.length > 0) {
      generateChartData(logs);
    } else {
      // Clear chart when no logs
      setAllChartData([]);
      setChartData([]);
    }
  }, [logs, dateRange]);

  const handleChartClick = (dataPoint: ChartDataPoint) => {
    const clickedDate = new Date(dataPoint.timestamp);
    const dayStart = startOfDay(clickedDate);
    const dayEnd = endOfDay(clickedDate);
    
    const dayLogs = logs.filter(log => {
      const logTime = new Date(log.timestamp);
      return logTime >= dayStart && logTime <= dayEnd;
    });
    
    setFilteredLogs(dayLogs);
    toast({
      title: "Filtered by date",
      description: `Showing ${dayLogs.length} logs from ${format(dayStart, 'MMM dd')}`,
    });
  };

  // Touch navigation for chart
  const { containerRef } = useTouchNavigation({
    onSwipeLeft: handleNextWindow,
    onSwipeRight: handlePreviousWindow,
    enabled: true,
    threshold: 50
  });

  // Update chart window when data changes
  React.useEffect(() => {
    if (allChartData.length > 0) {
      updateChartWindow(allChartData, chartWindowStart);
    }
  }, [allChartData, chartWindowStart, chartWindowSize]);

  return (
    <div className="px-6 py-6">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold">System Logs</h1>
          <p className="text-muted-foreground mt-1">
            Monitor and analyze system events and activities
          </p>
          {isProcessingAI && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground mt-2">
              <RefreshCw className="h-4 w-4 animate-spin" />
              AI processing logs...
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <Button onClick={exportLogs} variant="outline" className="gap-2">
            <Download className="h-4 w-4" />
            Export
          </Button>
          <Button onClick={fetchLogs} variant="outline" className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
        </div>
      </div>

      <div className="space-y-6">

      {/* Time Series Chart */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Log Volume Over Time</h3>
          <div className="flex items-center gap-2">
            {/* Quick Navigation */}
            <div className="flex gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleQuickNavigation('today')}
                className="text-xs"
              >
                Today
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleQuickNavigation('week')}
                className="text-xs"
              >
                Week
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleQuickNavigation('month')}
                className="text-xs"
              >
                Month
              </Button>
            </div>
            
            {/* Window Navigation */}
            {allChartData.length > chartWindowSize && (
              <div className="flex items-center gap-1 ml-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePreviousWindow}
                  disabled={chartWindowStart === 0}
                  className="h-8 w-8 p-0"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-xs text-muted-foreground px-2">
                  {chartWindowStart + 1}-{Math.min(chartWindowStart + chartWindowSize, allChartData.length)} of {allChartData.length}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleNextWindow}
                  disabled={chartWindowStart + chartWindowSize >= allChartData.length}
                  className="h-8 w-8 p-0"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        </div>
        <div 
          ref={containerRef}
          className="relative touch-pan-y select-none transition-transform duration-200"
          style={{ 
            touchAction: 'pan-y pinch-zoom',
            WebkitUserSelect: 'none',
            WebkitTouchCallout: 'none'
          }}
        >
          <LogVolumeSVGChart 
            data={chartData} 
            onPointClick={handleChartClick}
          />
          
          {/* Touch hint for mobile users */}
          <div className="absolute inset-0 pointer-events-none opacity-0 transition-opacity duration-300 hover:opacity-100 md:hidden">
            <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 text-xs text-muted-foreground bg-background/80 px-2 py-1 rounded">
              Swipe left/right to navigate
            </div>
          </div>
        </div>
      </Card>

      {/* Filters */}
      <div className="py-4 bg-background">
        <div className="space-y-4">
          {/* Primary filters row */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 min-w-0">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search logs..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 h-9"
                />
              </div>
            </div>
            
            <div className="flex gap-2">
              <Select value={selectedLevel} onValueChange={setSelectedLevel}>
                <SelectTrigger className="w-[120px] h-9">
                  <SelectValue placeholder="Level" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Levels</SelectItem>
                  <SelectItem value="debug">Debug</SelectItem>
                  <SelectItem value="info">Info</SelectItem>
                  <SelectItem value="warn">Warning</SelectItem>
                  <SelectItem value="error">Error</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>

              <Select value={selectedSource} onValueChange={setSelectedSource}>
                <SelectTrigger className="w-[140px] h-9">
                  <SelectValue placeholder="Source" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sources</SelectItem>
                  {Object.entries(sourceTypeLabels).map(([key, label]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {uniqueCategories.length > 0 && (
                <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                  <SelectTrigger className="w-[130px] h-9">
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {uniqueCategories.map(category => (
                      <SelectItem key={category} value={category}>{category}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {uniqueDevices.length > 0 && (
                <Select value={selectedDevice} onValueChange={setSelectedDevice}>
                  <SelectTrigger className="w-[160px] h-9">
                    <SelectValue placeholder="Device" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Devices</SelectItem>
                    {uniqueDevices.map(device => (
                      <SelectItem key={device.id} value={device.id}>{device.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="gap-2 h-9 px-3">
                    <CalendarIcon className="h-4 w-4" />
                    <span className="hidden sm:inline">
                      {format(dateRange.from, 'MMM dd')} - {format(dateRange.to, 'MMM dd')}
                    </span>
                    <span className="sm:hidden">
                      Date
                    </span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="end">
                  <div className="p-4 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium">From</label>
                        <Calendar
                          mode="single"
                          selected={dateRange.from}
                          onSelect={(date) => date && setDateRange(prev => ({ ...prev, from: startOfDay(date) }))}
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium">To</label>
                        <Calendar
                          mode="single"
                          selected={dateRange.to}
                          onSelect={(date) => date && setDateRange(prev => ({ ...prev, to: endOfDay(date) }))}
                        />
                      </div>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* Results summary */}
          {filteredLogs.length !== logs.length && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Filter className="h-4 w-4" />
              Showing {filteredLogs.length} of {logs.length} logs
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => {
                  setSearchTerm('');
                  setSelectedLevel('all');
                  setSelectedSource('all');
                  setSelectedCategory('all');
                  setSelectedDevice('all');
                }}
                className="h-6 px-2 text-xs"
              >
                Clear filters
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Logs Table */}
      <div className="bg-background rounded-lg border">
        {loading ? (
          <div className="flex justify-center py-8">
            <RefreshCw className="h-6 w-6 animate-spin" />
          </div>
        ) : (
          <>
            {/* Table Header */}
            <div className="border-b bg-muted/50 px-4 py-3">
              <h3 className="text-lg font-semibold">
                System Logs
                <span className="ml-2 text-sm font-normal text-muted-foreground">
                  ({filteredLogs.length} entries)
                </span>
              </h3>
            </div>

            {/* Logs Content */}
            <div className="max-h-[600px] overflow-auto">
              {filteredLogs.length > 0 ? (
                <div className="divide-y">
                  {filteredLogs.map((log) => {
                    const LevelIcon = logLevelIcons[log.log_level as keyof typeof logLevelIcons] || Info;
                    return (
                      <div key={log.id} className="p-4 hover:bg-muted/30 transition-colors">
                        <div className="flex items-start gap-3">
                          {/* Timestamp and Level */}
                          <div className="flex-shrink-0 text-xs text-muted-foreground min-w-[100px]">
                            {format(new Date(log.timestamp), 'HH:mm:ss')}
                          </div>
                          
                          {/* Level Badge */}
                          <div className="flex-shrink-0">
                            <Badge className={`gap-1 text-xs ${logLevelColors[log.log_level as keyof typeof logLevelColors]}`}>
                              <LevelIcon className="h-3 w-3" />
                              {log.log_level.toUpperCase()}
                            </Badge>
                          </div>

                          {/* Source and Event */}
                          <div className="flex-shrink-0 text-xs text-muted-foreground min-w-[120px]">
                            <div className="font-medium">
                              {sourceTypeLabels[log.source_type as keyof typeof sourceTypeLabels] || log.source_type}
                            </div>
                            <div className="text-xs opacity-75">{log.event_type}</div>
                          </div>

                          {/* Device Information */}
                          {log.devices && (
                            <div className="flex-shrink-0 min-w-[150px]">
                              <Link 
                                to={`/device/${log.device_id}`}
                                className="flex items-center gap-2 text-sm font-medium text-primary hover:underline group"
                              >
                                <Laptop className="h-4 w-4 transition-transform group-hover:scale-110" />
                                <div className="flex flex-col gap-0.5">
                                  <span className="line-clamp-1">{log.devices.name}</span>
                                  <span className="text-xs text-muted-foreground line-clamp-1">
                                    {log.devices.hostname}
                                  </span>
                                </div>
                              </Link>
                            </div>
                          )}

                          {/* Message Content */}
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-mono text-foreground leading-relaxed">
                              {log.message}
                            </div>
                            
                            {log.ai_summary && (
                              <div className="mt-1 text-xs text-muted-foreground">
                                <span className="font-medium">AI Summary:</span> {log.ai_summary}
                              </div>
                            )}

                            {/* Duration and Status */}
                            {(log.duration_ms || log.status_code) && (
                              <div className="mt-1 flex gap-3 text-xs text-muted-foreground">
                                {log.duration_ms && (
                                  <span>{log.duration_ms}ms</span>
                                )}
                                {log.status_code && (
                                  <span className={log.status_code >= 400 ? 'text-red-600' : 'text-green-600'}>
                                    {log.status_code}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>

                          {/* AI Category and Tags */}
                          <div className="flex-shrink-0 text-right">
                            {log.ai_category && (
                              <Badge variant="secondary" className="mb-1 text-xs">
                                {log.ai_category}
                              </Badge>
                            )}
                            
                            {log.ai_tags && log.ai_tags.length > 0 && (
                              <div className="flex flex-wrap gap-1 justify-end">
                                {log.ai_tags.slice(0, 2).map((tag, index) => (
                                  <Badge key={index} variant="outline" className="text-xs">
                                    {tag}
                                  </Badge>
                                ))}
                                {log.ai_tags.length > 2 && (
                                  <Badge variant="outline" className="text-xs">
                                    +{log.ai_tags.length - 2}
                                  </Badge>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="text-muted-foreground mb-3">
                    {logs.length === 0 ? 'No logs available' : 'No logs match your current filters'}
                  </div>
                  {logs.length > 0 && (
                    <Button 
                      variant="outline" 
                      onClick={() => {
                        setSearchTerm('');
                        setSelectedLevel('all');
                        setSelectedSource('all');
                        setSelectedCategory('all');
                        setSelectedDevice('all');
                      }}
                    >
                      Clear all filters
                    </Button>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>
      </div>
    </div>
  );
};

export default LogsPage;